<template>
  <div class="carousel-container">
    <!-- rows: the one PRO-550 layout that is structurally different (full-width list) rather than a
         styling variant of the same card, so it gets its own markup; carousel/grid/hero otherwise all
         render as the existing scrolling card row, just enriched with the product details when present. -->
    <div v-if="layout === 'rows'" class="carousel-rows">
      <a v-for="(card, i) in cards"
         :key="i"
         class="carousel-row"
         :href="cardHref(card)"
         target="_blank"
         rel="noopener noreferrer"
      >
        <img v-if="card.image_url" :src="card.image_url" class="carousel-row-thumb"/>
        <div class="carousel-row-body">
          <p v-if="card.subtitle" class="carousel-card-brand">{{card.subtitle}}</p>
          <p v-if="card.title" class="carousel-card-title">{{card.title}}</p>
          <p v-if="productOf(card)" class="carousel-card-price">
            <span>{{productOf(card).price}}</span>
            <s v-if="productOf(card).price_original">{{productOf(card).price_original}}</s>
            <span v-if="productOf(card).discount_percent" class="carousel-card-discount">
              &minus;{{productOf(card).discount_percent}}&nbsp;%
            </span>
          </p>
        </div>
      </a>
    </div>

    <!-- hero: the lead recommendation gets the full card treatment; weaker matches follow as rows -->
    <div v-else-if="layout === 'hero'" class="carousel-hero">
      <a :href="cardHref(lead)" target="_blank" rel="noopener noreferrer" class="carousel-card-link carousel-hero-lead">
        <div class="carousel-card-detail">
          <div class="carousel-card-image">
            <img v-if="lead.image_url" :src="lead.image_url"/>
            <span v-if="productOf(lead) && productOf(lead).discount_percent" class="carousel-card-badge">
              &minus;{{productOf(lead).discount_percent}}&nbsp;%
            </span>
          </div>
          <p v-if="lead.subtitle" class="carousel-card-brand">{{lead.subtitle}}</p>
          <p v-if="lead.title" class="carousel-card-title">{{lead.title}}</p>
          <p v-if="productOf(lead)" class="carousel-card-price">
            <span>{{productOf(lead).price}}</span>
            <s v-if="productOf(lead).price_original">{{productOf(lead).price_original}}</s>
          </p>
          <p v-else-if="lead.text" class="carousel-card-content">{{lead.text}}</p>
          <p v-if="productOf(lead) && productOf(lead).labels" class="carousel-card-labels">
            {{productOf(lead).labels.join(' · ')}}
          </p>
          <ul v-if="optionsOf(lead)" class="carousel-card-options">
            <li v-for="value in optionsOf(lead).values" :key="value">{{value}}</li>
            <li v-if="moreOptions(lead)" class="more">+{{moreOptions(lead)}}</li>
          </ul>
        </div>
      </a>

      <div v-if="alternatives.length" class="carousel-rows carousel-hero-alts">
        <a v-for="(card, i) in alternatives"
           :key="i"
           class="carousel-row"
           :href="cardHref(card)"
           target="_blank"
           rel="noopener noreferrer"
        >
          <img v-if="card.image_url" :src="card.image_url" class="carousel-row-thumb"/>
          <div class="carousel-row-body">
            <p v-if="card.subtitle" class="carousel-card-brand">{{card.subtitle}}</p>
            <p v-if="card.title" class="carousel-card-title">{{card.title}}</p>
            <p v-if="productOf(card)" class="carousel-card-price">
              <span>{{productOf(card).price}}</span>
              <s v-if="productOf(card).price_original">{{productOf(card).price_original}}</s>
              <span v-if="productOf(card).discount_percent" class="carousel-card-discount">
                &minus;{{productOf(card).discount_percent}}&nbsp;%
              </span>
            </p>
          </div>
        </a>
      </div>
    </div>

    <div v-else class="carousel"
         :class="{ 'flex justify-end': justifyEnd }"
         ref="carousel"
         @scroll="handleScroll(false)"
    >
      <div v-for="(card, i) in cards"
           :key="i"
           class="carousel-card"
      >
        <a :href="cardHref(card)" target="_blank" rel="noopener noreferrer" class="carousel-card-link">
          <div class="carousel-card-detail">
            <div class="carousel-card-image">
              <img :src="card.image_url"/>
              <span v-if="productOf(card) && productOf(card).discount_percent" class="carousel-card-badge">
                &minus;{{productOf(card).discount_percent}}&nbsp;%
              </span>
            </div>
            <p v-if="card.subtitle" class="carousel-card-brand">{{card.subtitle}}</p>
            <p v-if="card.title" class="carousel-card-title">{{card.title}}</p>
            <p v-if="productOf(card)" class="carousel-card-price">
              <span>{{productOf(card).price}}</span>
              <s v-if="productOf(card).price_original">{{productOf(card).price_original}}</s>
            </p>
            <p v-else-if="card.text" class="carousel-card-content">{{card.text}}</p>
            <p v-if="productOf(card) && productOf(card).labels" class="carousel-card-labels">
              {{productOf(card).labels.join(' · ')}}
            </p>
            <ul v-if="optionsOf(card)" class="carousel-card-options">
              <li v-for="value in optionsOf(card).values" :key="value">{{value}}</li>
              <li v-if="moreOptions(card)" class="more">+{{moreOptions(card)}}</li>
            </ul>
          </div>
        </a>

        <div v-if="card.buttons" class="carousel-card-buttons">
          <a v-for="(button, idx) in card.buttons"
             :key="idx"
             :href="buttonHref(button)"
             target="_blank"
             rel="noopener noreferrer"
             class="button hover:cursor-pointer text-gray-bubble hover:bg-gray-bubble hover:text-white"
          >
            <span>{{button.title}}</span>
          </a>
        </div>
      </div>
    </div>

    <template v-if="layout !== 'rows' && layout !== 'hero'">
      <div v-if="showLeftArrow"
           class="carousel-arrow-container"
           @click="scrollLeft"
      >
        <button class="carousel-arrow">
          <vs-icon icon="arrow_back" size="1rem"/>
        </button>
      </div>

      <div v-if="showRightArrow"
           class="carousel-arrow-container right-0"
           @click="scrollRight"
      >
        <button class="carousel-arrow">
          <vs-icon icon="arrow_forward right-0" size="1rem"/>
        </button>
      </div>
    </template>

  </div>
</template>

<script>

export default {
  name: 'CarouselPreview',
  props: {
    payload: {
      type: Array,
      required: true
    },
    // PRO-550: which layout the engine chose for this answer, off `message.metadata.product_layout`.
    // Unknown/absent falls back to the plain carousel, same as an engine that predates the field.
    layout: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      showLeftArrow: false,
      showRightArrow: false,
      justifyEnd: false
    }
  },
  computed: {
    // The API only carries title/text/image_url/buttons on a structure element; everything else (brand,
    // both prices, discount, labels, sizes, item_url) rides along as `details` (PRO-550) and gets merged
    // back in here so the template can read it like any other card field.
    cards() {
      return (this.payload || []).map(card => {
        if (!card || !card.details) return card
        const { details, ...element } = card
        return { ...element, ...details }
      })
    },

    // hero is the one layout that treats the first card differently: it is THE recommendation
    lead() {
      return this.cards[0] || {}
    },
    alternatives() {
      return this.cards.slice(1)
    }
  },
  methods: {
    productOf(card) {
      return card && card.product
    },
    // Sizes exist as an array only on a merged product (PRO-225); matches the widget's own
    // options()/moreOptions() (ProductCards.vue) so the same collapsed "+n" chip shows here.
    optionsOf(card) {
      const options = this.productOf(card) && this.productOf(card).options
      return options && options.values && options.values.length ? options : null
    },
    moreOptions(card) {
      const options = this.optionsOf(card)
      return options && options.total > options.values.length ? options.total - options.values.length : 0
    },
    cardHref(card) {
      if (card.item_url) return this.safeUrl(card.item_url)
      return this.buttonHref((card.buttons || [])[0])
    },
    buttonHref(button) {
      if (!button) return null
      return button.type === 'phone' ? `tel:${this.safePhone(button.payload)}` : this.safeUrl(button.payload)
    },
    // Card data (item_url, button payload) comes straight off a third-party feed via the engine — never
    // trust it as a navigable URL. A `javascript:`/`data:` value would otherwise execute in the admin's
    // own origin on click; only http(s) links are ever rendered as a real href.
    safeUrl(url) {
      if (!url) return null
      try {
        return ['http:', 'https:'].includes(new URL(url, window.location.origin).protocol) ? url : null
      } catch (err) {
        return null
      }
    },
    safePhone(payload) {
      return String(payload || '').replace(/[^0-9+().\- ]/g, '')
    },
    handleScroll(calledFromMounted) {
      const carousel = this.$refs.carousel
      if (!carousel) return
      this.showLeftArrow = carousel.scrollLeft > 0
      this.showRightArrow = carousel.scrollLeft < (carousel.scrollWidth - carousel.clientWidth - 1)

      if (calledFromMounted && !this.showRightArrow) {
        this.justifyEnd = true
      }
    },
    scrollLeft() {
      const carousel = this.$refs.carousel
      carousel.scrollBy({ left: -160, behavior: 'smooth' })
    },
    scrollRight() {
      const carousel = this.$refs.carousel
      carousel.scrollBy({ left: 160, behavior: 'smooth' })
    }
  },
  mounted() {
    this.handleScroll(true) // Initial check to set arrow visibility
  }
}
</script>

<style lang="scss" scoped>
.carousel-container {
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  height: 100%;
  scrollbar-width: none;
  flex-direction: column;
  position: relative;
}

.carousel-arrow-container {
  margin: 8px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
}

.carousel-arrow {
  border: none;
  background: rgb(234, 234, 234);
  box-shadow: 0 0 10px 6px white;
  height: 30px;
  width: 30px;
  border-radius: 50px;

  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.carousel-arrow:hover {
  box-shadow: 0 0 10px 5px rgba(200, 200, 200, 0.8);
}

.carousel {
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  height: 100%;
  scrollbar-width: none;
  align-items: stretch;
  flex-direction: row;
  position: relative;
  gap: 16px;

  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
  scrollbar-width: none; /* Hide scrollbar for Firefox */
  -ms-overflow-style: none; /* Hide scrollbar for Internet Explorer and Edge */

  /* Hide scrollbar for WebKit browsers (Chrome, Safari) */
  &::-webkit-scrollbar {
    display: none; /* Hide scrollbar */
  }
}

.carousel-card {
  flex: 0 0 auto;
  width: 200px;
  border-radius: 10px;
  border: rgb(234, 234, 234) solid 1px;
  overflow: hidden;
  background-color: #fff;
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.carousel-card-link {
  display: block;
  color: inherit;
  text-decoration: none;
  font-weight: normal;
  text-align: left;
  line-height: normal;
}

.carousel-card-detail {
  img {
    width: 100%;
    height: 150px;
    object-fit: cover;
  }
}
.carousel-card-image {
  position: relative;
}
.carousel-card-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  background: #e34850;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
}
.carousel-card-brand {
  color: rgb(150, 150, 150);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  margin: 10px 0 0 0;
  padding: 0 20px;
}
.carousel-card-title {
  font-weight: 700;
  font-size: 14px;
  margin: 10px 0 0 0;
  padding: 0 20px;
}
.carousel-card-content {
  margin: 5px 0 0 0;
  padding: 0 20px;
  font-size: 14px;
  font-weight: 300;
}
.carousel-card-price {
  margin: 5px 0 0 0;
  padding: 0 20px;
  font-size: 14px;
  font-weight: 700;

  s {
    margin-left: 6px;
    font-weight: 300;
    color: rgb(150, 150, 150);
  }
}
.carousel-card-discount {
  margin-left: 6px;
  color: #e34850;
  font-weight: 700;
}
.carousel-card-labels {
  margin: 5px 0 0 0;
  padding: 0 20px 10px 20px;
  font-size: 12px;
  color: rgb(150, 150, 150);
}
.carousel-card-options {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 0;
  padding: 0 20px 10px 20px;
  list-style: none;

  li {
    border: rgb(234, 234, 234) solid 1px;
    border-radius: 4px;
    padding: 1px 6px;
    font-size: 11px;
    color: rgb(100, 100, 100);
  }

  li.more {
    border: none;
    padding: 1px 2px;
    color: rgb(150, 150, 150);
  }
}

.carousel-hero {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}
.carousel-hero-lead {
  border-radius: 10px;
  border: rgb(234, 234, 234) solid 1px;
  overflow: hidden;
  background-color: #fff;
  display: block;
  width: 100%;

  .carousel-card-detail img {
    height: 180px;
  }
}
.carousel-hero-alts {
  padding-top: 4px;
  border-top: rgb(234, 234, 234) solid 1px;
}

.carousel-rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}
.carousel-row {
  display: flex;
  align-items: center;
  gap: 12px;
  color: inherit;
  text-decoration: none;
  font-weight: normal;
  text-align: left;
  line-height: normal;
  border: rgb(234, 234, 234) solid 1px;
  border-radius: 10px;
  padding: 8px;
  background-color: #fff;
}
.carousel-row-thumb {
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 6px;
  flex: 0 0 auto;
}
.carousel-row-body {
  flex: 1 1 auto;
  min-width: 0;

  .carousel-card-brand, .carousel-card-title, .carousel-card-price {
    padding: 0;
    margin: 2px 0 0 0;
  }
}

.carousel-card-buttons {
  display: flex;
  flex-direction: column;
  border-top: rgb(234, 234, 234) solid 1px;
  margin-top: 10px;

  > .button {
    border-bottom: rgb(234, 234, 234) solid 1px;
  }

  > .button:last-child {
    border-bottom: none;
  }
}
.button {
  border: none;
  width: 100%;
  margin: 0;
  font-size: 12px;
  font-family: inherit;
  text-decoration: none;
  text-align: center;
  display: inline-block;
  min-height: 40px;
  min-width: 90px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
a {
  text-decoration: none;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 30px;
  text-align: center;
}
</style>
